import { useCallback, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import * as v from 'valibot';
import type { RootState } from '@redux/RootReducer';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { DEFAULT_MONTH_RANGE, getLocalTimeZone, toZonedDayIso } from '@/utils/date';
import { cleanNuqsURLParams } from '@/utils/url';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';

import { normalizePricingsFilters, normalizePricingsFiltersUpdate } from '../data-provider';
import { type PricingsFilters, type PricingsFiltersApi, pricingsFiltersApiSchema } from '../models';

const DEFAULT_TIME_ZONE = getLocalTimeZone();
const DEFAULT_EFFECTIVE_DATE = toZonedDayIso(DEFAULT_TIME_ZONE);
const DEFAULT_EXPIRATION_DATE = toZonedDayIso(DEFAULT_TIME_ZONE, DEFAULT_MONTH_RANGE, true);

const parsers = {
  branchKey: parseAsInteger,
  cargoType: parseAsString,
  carrierKey: parseAsInteger,
  currencyCode: parseAsString,
  effectiveDate: parseAsString.withDefault(DEFAULT_EFFECTIVE_DATE),
  expirationDate: parseAsString.withDefault(DEFAULT_EXPIRATION_DATE),
  modeType: parseAsString,
  packageType: parseAsString,
  pricingBasis: parseAsString,
  pricingKey: parseAsInteger,
  pricingType: parseAsString,
  serviceKey: parseAsInteger,
  trailerKey: parseAsInteger,
};

export const getDefaultPricingsFormValues = (timeZone = getLocalTimeZone()): PricingsFilters => ({
  branch: null,
  cargoType: null,
  carrier: null,
  currency: null,
  effectiveFrom: toZonedDayIso(timeZone),
  effectiveTo: toZonedDayIso(timeZone, DEFAULT_MONTH_RANGE, true),
  equipmentType: null,
  packingCode: null,
  pricingBasis: null,
  pricingKey: null,
  pricingType: null,
  service: null,
  transportationMode: null,
});

export const usePricingsFilters = () => {
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany, shallowEqual);
  const [rawSearchParams, setSearchParams] = useQueryStates(parsers, {
    clearOnDefault: false,
    history: 'replace',
  });

  const customerKey = selectedCompany?.companyId ?? 0;

  const searchParams = useMemo(() => {
    const result = v.safeParse(pricingsFiltersApiSchema, cleanNuqsURLParams(rawSearchParams));

    if (!result.success) {
      console.error('Invalid URL params', result.issues);
      showNotification({ message: 'Invalid URL params', type: NOTIFICATION_TYPES.ERROR });
      return {
        effectiveDate: parsers.effectiveDate.defaultValue,
        expirationDate: parsers.expirationDate.defaultValue,
      } as PricingsFiltersApi;
    }

    return result.output;
  }, [rawSearchParams]);

  const { data: filters } = useQuery({
    placeholderData: keepPreviousData,
    queryFn: () => normalizePricingsFilters(searchParams),
    queryKey: ['pricings-filters', searchParams],
    // biome-ignore lint/style/noMagicNumbers: 1 hour
    staleTime: 1000 * 60 * 60,
  });

  const onFiltersChange = (updates: Partial<PricingsFilters>) => {
    const nextSearchParams = normalizePricingsFiltersUpdate({
      ...getDefaultPricingsFormValues(),
      ...filters,
      ...updates,
    });

    setSearchParams(
      Object.entries(nextSearchParams).reduce<Record<string, unknown>>((acc, [key, value]) => {
        if (value !== undefined && value !== '') {
          acc[key] = value;
        } else {
          acc[key] = null;
        }
        return acc;
      }, {}) as Parameters<typeof setSearchParams>[0],
    );
  };

  const clearFilters = useCallback(() => {
    return setSearchParams(
      Object.fromEntries(Object.keys(parsers).map((key) => [key, null])) as Parameters<typeof setSearchParams>[0],
    );
  }, [setSearchParams]);

  return {
    clearFilters,
    customerKey,
    filters,
    onFiltersChange,
  };
};
